import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
  LinearProgress,
} from '@mui/material';
import { Clock, Video, FileText, AlertTriangle, Square, Circle } from 'lucide-react';
import videoCallPreview from '../../../../../../assets/images/graphics/video_call_preview.png';

export const StartRecordingCard: React.FC = () => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(22);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  /* ── Countdown when recording starts ── */
  useEffect(() => {
    if (recording) {
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s <= 1) {
            clearInterval(timerRef.current);
            setRecording(false);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [recording]);

  const pad = (n: number) => String(n).padStart(2, '0');
  const progress = ((22 - seconds) / 22) * 100;

  return (
    <Paper
      variant='outlined'
      sx={{
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      {/* ── Question header ── */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Stack direction='row' spacing={1} sx={{ mb: 1 }}>
          <Chip label='Question 1' size='small' variant='outlined' sx={{ fontSize: 10, height: 20 }} />
          <Chip label='Skill' size='small' color='success' sx={{ fontSize: 10, height: 20, fontWeight: 600 }} />
        </Stack>
        <Typography variant='subtitle2' fontWeight={600} color='text.primary'>
          Explain the role of React in frontend development as an AEM Developer.
        </Typography>
      </Box>

      {/* ── Two-panel layout ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
          px: 2.5,
          pb: 2,
        }}
      >
        {/* Left – Question reference panel */}
        <Stack spacing={1}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.100',
              borderRadius: 1.5,
              px: 1.5,
              py: 0.5,
            }}
          >
            <FileText size={12} color='#1976d2' />
            <Typography variant='caption' fontWeight={700} color='primary.dark'>
              Question Reference
            </Typography>
          </Box>

          <Paper
            variant='outlined'
            sx={{
              bgcolor: 'action.hover',
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderRadius: 2,
              aspectRatio: '4/3',
              overflow: 'hidden',
            }}
          >
            <Box>
              <Chip
                label='SCENARIO'
                size='small'
                sx={{
                  fontSize: 8,
                  height: 16,
                  fontWeight: 700,
                  bgcolor: 'primary.100',
                  color: 'primary.dark',
                }}
              />
            </Box>

            <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10, lineHeight: 1.4 }}>
              You are a frontend developer joining a team that currently uses a
              monolithic jQuery codebase. The team is planning a migration to a
              modern component-based architecture.
            </Typography>

            <Stack
              spacing={0.5}
              sx={{
                borderLeft: '2px solid',
                borderColor: 'primary.light',
                pl: 1,
              }}
            >
              {[
                'Project has 50,000+ lines of legacy JS',
                'Team of 6 developers, mixed experience',
                'No existing test suite',
              ].map((point, i) => (
                <Typography key={i} variant='caption' color='text.secondary' sx={{ fontSize: 9 }}>
                  • {point}
                </Typography>
              ))}
            </Stack>

            <Box
              sx={{
                mt: 'auto',
                bgcolor: 'warning.50',
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 1,
                p: 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <AlertTriangle size={12} color='#ed6c02' style={{ flexShrink: 0, marginTop: 2 }} />
              <Typography variant='caption' color='warning.dark' sx={{ fontSize: 9, lineHeight: 1.3 }}>
                Reference this scenario in your answer. No single correct answer exists.
              </Typography>
            </Box>
          </Paper>
        </Stack>

        {/* Right – Camera preview + controls */}
        <Stack spacing={1}>
          <Box
            sx={{
              position: 'relative',
              bgcolor: 'common.black',
              borderRadius: 2,
              overflow: 'hidden',
              aspectRatio: '4/3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component='img'
              src={videoCallPreview}
              alt='Professional Video Call Preview'
              sx={{ height: '100%', width: 'auto', opacity: 0.9, objectFit: 'contain' }}
            />
            {recording && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                  }}
                />
                <Typography variant='caption' color='common.white' fontWeight={700} sx={{ fontSize: 9 }}>
                  REC
                </Typography>
              </Box>
            )}
          </Box>

          <Button
            onClick={() => {
              setSeconds(22);
              setRecording((r) => !r);
            }}
            variant='contained'
            color={recording ? 'error' : 'success'}
            size='small'
            startIcon={recording ? <Square size={14} /> : <Circle size={14} />}
            sx={{
              borderRadius: 5,
              fontSize: 11,
              fontWeight: 700,
              py: 0.75,
              textTransform: 'none',
            }}
          >
            {recording ? 'Stop Recording' : 'Start Recording'}
          </Button>
        </Stack>
      </Box>

      {/* ── Bottom stats bar ── */}
      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        sx={{ px: 2.5, pb: 2 }}
      >
        {/* Timer */}
        <Box sx={{ flex: 1 }}>
          <LinearProgress
            variant='determinate'
            value={recording ? progress : 0}
            sx={{ height: 4, borderRadius: 2, mb: 1 }}
          />
          <Stack direction='row' spacing={1} alignItems='center'>
            <Clock size={14} color='#1976d2' />
            <Typography variant='body2' fontWeight={700} color='text.primary'>
              {pad(Math.floor(seconds / 60))}:{pad(seconds % 60)}
            </Typography>
          </Stack>
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Remaining Time
          </Typography>
        </Box>

        {/* Skill questions */}
        <Paper
          variant='outlined'
          sx={{
            px: 2,
            py: 1,
            textAlign: 'center',
            borderRadius: 2,
            borderColor: 'primary.light',
            minWidth: 80,
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 8, textTransform: 'uppercase' }}>
            Skill Questions
          </Typography>
          <Typography variant='h6' fontWeight={800} color='text.primary' sx={{ lineHeight: 1 }}>
            1
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 8, textTransform: 'uppercase' }}>
            Left to Answer
          </Typography>
        </Paper>

        {/* Video tips */}
        <Stack spacing={0.5} sx={{ minWidth: 100 }}>
          <Stack direction='row' spacing={0.5} alignItems='center'>
            <Video size={12} color='#2e7d32' />
            <Typography variant='caption' fontWeight={700} color='text.primary' sx={{ fontSize: 9, textTransform: 'uppercase' }}>
              Video Tips
            </Typography>
          </Stack>
          {[
            'Stable internet required',
            'Quiet, well-lit environment',
            'Re-recording allowed',
          ].map((tip, i) => (
            <Stack key={i} direction='row' spacing={0.5} alignItems='center'>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: 9 }}>
                {tip}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};
